/**
 * 手入力取引閲覧モーダルの契約明細テーブル。
 *
 * #6236 (Design Decision 2) で `BilateralTrade.legs` (repeated) が撤去され、
 * 旧 legs[0] の値は `BilateralTrade` 直下のフラット field になった。1 取引 =
 * 1 契約 = 1 数量・単価単位 (ADR trading-contract-one-to-one) のため、本
 * テーブルは常に 1 行のみ描画する (`#` 列は leg_seq の生値ではなく行番号 —
 * Design Decision 1 で leg_seq 列そのものを撤去したため)。
 */
import type { BilateralTrade } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
} from '@enechain/ecloud-designsystem'

import {
  TABLE_BODY_CELL_STYLE,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import {
  AreaCodeLabel,
  DeliveryCalendarKeyLabel,
  LegTimeZoneLabel,
} from '~/pages/SupplyBilateral/components/Badges'
import {
  decimalString,
  formatPeriodRange,
} from '~/pages/SupplyBilateral/models/formatters'

const FALLBACK = '—'

/**
 * **`MasterDataTable` には寄せず、生 table のままにする** (PR #8203 レビュー指摘
 * への回答 / ikdene)。共有するのは**見た目の single source だけ**で、一覧表の
 * 振る舞いは持ち込まない。
 *
 * `MasterDataTable` は一覧表向けに、行の並べ替え・ドラッグ (`canDragRows` /
 * `onRowDrag*` / `dragHandleColumn`)、行クリック、行アクション列、
 * `isLoading` / `emptyLabel` / `errorLabel` の 3 状態、`isRowDisabled`、sticky
 * ヘッダを抱えている。本表は**取引 1 件の内訳で常に 1 行**、並べ替え・クリック・
 * 空状態のいずれも無いため、`columns` 定義 (`cell: (row) => ...` を 9 本) に
 * 移しても行数はさほど減らず、使わない機能への依存だけが増える。
 *
 * 見た目の drift は `TABLE_HEADER_CELL_STYLE` / `TABLE_BODY_CELL_STYLE` が
 * design の `.ecl-table th` / `td` と同値であることで防ぐ。
 */

/**
 * 見出しの見た目は **`TABLE_HEADER_CELL_STYLE` (single source) をそのまま当てる**。
 *
 * ⚠ **DS の `TableColumnHeader` は地色を持たない。** 素の Chakra 既定なので、
 * 地・padding・太さ・罫線・`Text.midEmphasis` はこの repo 側の定数が補っている
 * (`MasterDataTable` の JSDoc:「書き写すと表ごとに見出しの見た目がずれるため、
 * この 1 箇所を single source とし、**生 table を使う画面からも import して使う**」)。
 * DS に委ねると**地色が消える** (PR #8203 レビュー指摘で実際に踏んだ)。
 *
 * 2026-09-16 デザイナー指摘「modal 上の table の TH が DS 違反」の実体は
 * 「inline で書いたこと」ではなく **single source を import せず手で書き写して
 * いたこと**だった (旧実装は `fontWeight: 600` / `Text.midEmphasis` /
 * `border-bottom` を独自に持ち、**地色だけ写し忘れていた**)。
 *
 * `textAlign` は列ごとに変わるので定数に含まれない (呼び出し側で指定する)。
 */
const HEADER_CELL_STYLE = TABLE_HEADER_CELL_STYLE

/** 本文セルも同じく single source (`.ecl-table td`) をそのまま当てる。 */
const BODY_CELL_STYLE = TABLE_BODY_CELL_STYLE

export const LegDetailTable = ({
  trade,
}: {
  trade: BilateralTrade
}): React.ReactElement => {
  const { t } = useTranslation()
  const label = (key: string): string =>
    t(`page:supplyBilateral.manualDetail.legTable.${key}`)

  const volume = decimalString(trade.volumeMw)
  const unitPrice = decimalString(trade.unitPriceYenPerKwh)

  return (
    <Box overflowX="auto">
      <TableRoot fontSize="md" size="sm">
        <TableHeader>
          <TableRow>
            <TableColumnHeader style={HEADER_CELL_STYLE}>
              {label('legSeq')}
            </TableColumnHeader>
            {/* 列揃えは UI 辞書 table.md「列揃えの規約」: 固定値 (売買・エリア・時間帯・
                精算単価参照エリア・カレンダー) は TH/TD とも center、 数値 (数量・単価) は
                right。 一覧 (TradeRow) と同じ揃え。 */}
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}
            >
              {label('buySell')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}
            >
              {label('area')}
            </TableColumnHeader>
            <TableColumnHeader style={HEADER_CELL_STYLE}>
              {label('period')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}
            >
              {label('timeZoneType')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'right' }}
            >
              {label('volumeMw')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'right' }}
            >
              {label('unitPrice')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}
            >
              {label('settlementPriceArea')}
            </TableColumnHeader>
            <TableColumnHeader
              style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}
            >
              {label('calendarKey')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow key={trade.id !== '' ? trade.id : 'row-1'}>
            {/* 行番号 (常に 1) — leg_seq 列は撤去済み (Design Decision 1)。 */}
            <TableCell whiteSpace="nowrap" style={BODY_CELL_STYLE}>
              1
            </TableCell>
            <TableCell
              textAlign="center"
              whiteSpace="nowrap"
              style={BODY_CELL_STYLE}
            >
              <TradeSideLabel side={trade.buySell} />
            </TableCell>
            <TableCell
              textAlign="center"
              whiteSpace="nowrap"
              style={BODY_CELL_STYLE}
            >
              <AreaCodeLabel areaCode={trade.areaCode} />
            </TableCell>
            <TableCell whiteSpace="nowrap" style={BODY_CELL_STYLE}>
              {formatPeriodRange(
                trade.deliveryPeriodFrom,
                trade.deliveryPeriodTo,
              )}
            </TableCell>
            <TableCell
              textAlign="center"
              whiteSpace="nowrap"
              style={BODY_CELL_STYLE}
            >
              <LegTimeZoneLabel
                customBands={trade.customBands}
                timeZoneType={trade.timeZoneType}
              />
            </TableCell>
            <TableCell
              textAlign="right"
              whiteSpace="nowrap"
              fontVariantNumeric="tabular-nums"
              style={BODY_CELL_STYLE}
            >
              {volume === '' ? FALLBACK : volume}
            </TableCell>
            <TableCell
              textAlign="right"
              whiteSpace="nowrap"
              fontVariantNumeric="tabular-nums"
              style={BODY_CELL_STYLE}
            >
              {unitPrice === '' ? FALLBACK : unitPrice}
            </TableCell>
            <TableCell
              textAlign="center"
              whiteSpace="nowrap"
              style={BODY_CELL_STYLE}
            >
              <AreaCodeLabel areaCode={trade.settlementPriceAreaCode} />
            </TableCell>
            <TableCell
              textAlign="center"
              whiteSpace="nowrap"
              style={BODY_CELL_STYLE}
            >
              <DeliveryCalendarKeyLabel calendarKey={trade.calendarKey} />
            </TableCell>
          </TableRow>
        </TableBody>
      </TableRoot>
    </Box>
  )
}
